import { useCallback, useEffect, useRef, useState } from 'react'
import type { LeadProfileLead } from '../LeadProfileModal.types'

export function useLeadProfileManager(params: {
  lead: LeadProfileLead | undefined
  selectedFunnelId: number | null
  committedManagerId: number | null
}) {
  const { lead, selectedFunnelId, committedManagerId } = params

  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(
    null,
  )
  const selectedManagerIdRef = useRef<number | null>(null)
  const isManagerExplicitlyClearedRef = useRef(false)
  const isManagerDirtyRef = useRef(false)

  useEffect(() => {
    if (!lead) {
      setSelectedManagerId(null)
      selectedManagerIdRef.current = null
      isManagerExplicitlyClearedRef.current = false
      isManagerDirtyRef.current = false
      return
    }
    const next =
      selectedFunnelId !== null
        ? committedManagerId
        : lead.manager?.id ?? null

    // После server-side действий (например bulk-назначение менеджера) важно
    // синхронизировать селектор по актуальным данным воронки, даже если локально
    // ранее было "грязное" значение.
    if (next === selectedManagerIdRef.current && isManagerDirtyRef.current)
      return

    setSelectedManagerId(next)
    selectedManagerIdRef.current = next
    isManagerExplicitlyClearedRef.current = false
    isManagerDirtyRef.current = false
  }, [lead, selectedFunnelId, committedManagerId])

  const handleSelectManager = useCallback((managerId: number | null) => {
    setSelectedManagerId(managerId)
    selectedManagerIdRef.current = managerId
    isManagerDirtyRef.current = true
    if (managerId !== null) {
      isManagerExplicitlyClearedRef.current = false
    }
  }, [])

  const handleClearManager = useCallback(() => {
    isManagerExplicitlyClearedRef.current = true
    isManagerDirtyRef.current = true
    setSelectedManagerId(null)
    selectedManagerIdRef.current = null
  }, [])

  const resetManagerToCommitted = useCallback(() => {
    if (!lead) {
      setSelectedManagerId(null)
      selectedManagerIdRef.current = null
      isManagerExplicitlyClearedRef.current = false
      isManagerDirtyRef.current = false
      return
    }
    const next =
      selectedFunnelId !== null
        ? committedManagerId
        : lead.manager?.id ?? null
    setSelectedManagerId(next)
    selectedManagerIdRef.current = next
    isManagerExplicitlyClearedRef.current = false
    isManagerDirtyRef.current = false
  }, [committedManagerId, lead, selectedFunnelId])

  return {
    selectedManagerId,
    selectedManagerIdRef,
    isManagerExplicitlyClearedRef,
    handleSelectManager,
    handleClearManager,
    resetManagerToCommitted,
  }
}
